import { useState, useEffect } from 'react';
import PageLayout from '../../components/base/PageLayout';
import { FORM_ENDPOINT } from '../../config/form';
import { useShineClick } from '../../hooks/useShineClick';

export default function Contact() {
  const handleShineClick = useShineClick<HTMLButtonElement>();
  useEffect(() => {
    document.title = 'お問い合わせ - 講演依頼・研修・執事サービスのご相談｜執事ベル';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルへのお問い合わせフォーム。講演・セミナー依頼、企業研修のご相談、マナー講座のお申し込み、プライベート執事サービス、メディア出演・取材依頼など、各種ご依頼を承ります。全日本執事協会会長・執事ベルが、ご要望に合わせた最適なプランをご提案いたします。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'お問い合わせ, 講演依頼, 研修, 執事サービス, 相談');
    } else {
      const keywordsTag = document.createElement('meta');
      keywordsTag.name = 'keywords';
      keywordsTag.content = 'お問い合わせ, 講演依頼, 研修, 執事サービス, 相談';
      document.head.appendChild(keywordsTag);
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [formShownAt, setFormShownAt] = useState(() => Date.now());
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());
  const [hpValue, setHpValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.name || !formData.email || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    setSubmitStatus('idle');
    setSubmitErrorMessage('');

    if (!FORM_ENDPOINT) {
      setSubmitStatus('error');
      setSubmitErrorMessage('お問い合わせフォームは現在準備中です。メール（info@butlerbell.jp）でお問い合わせください。');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          form: 'contact',
          lang: 'ja',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service: formData.service,
          message: formData.message,
          _hp: hpValue,
          page: window.location.href,
          userAgent: navigator.userAgent,
          formShownAt,
          submissionId: submissionId
        })
      });
      const result = await response.json() as { status?: string };

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: ''
        });
        setFormShownAt(Date.now());
        setSubmissionId(crypto.randomUUID());
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setSubmitErrorMessage('送信に失敗しました。メール（info@butlerbell.jp）でお問い合わせください。');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitErrorMessage('送信に失敗しました。メール（info@butlerbell.jp）でお問い合わせください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageLayout hero={<>
<section className="relative py-20 md:py-32 bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: `url('/images/generated/seq-contact-hero-desk-e07c81.jpg')`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8 text-center">
          <h1 
            className="text-2xl md:text-8xl font-light text-white mb-6 md:mb-8 tracking-wide"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            お問い合わせ
          </h1>
          <p 
            className="text-sm md:text-2xl text-amber-400 font-light tracking-wider"
            style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
          >
            お気軽にご相談ください
          </p>
        </div>
      </section>
</>}>
      {/* Hero Section */}
      

      {/* Contact Form Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 
              className="text-xl md:text-5xl font-light text-[#1a1a1a] mb-4 md:mb-6"
              style={{ fontFamily: '"Noto Serif JP", serif' }}
            >
              Contact Form
            </h2>
            <p 
              className="text-sm md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto"
              style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
            >
              サービスに関するご質問、研修のご依頼、プライベート執事サービスのご相談など、<br className="hidden md:inline" />
              どのようなことでもお気軽にお問い合わせください。
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-600 text-xl md:text-2xl mr-3"></i>
                <div>
                  <h3 className="text-green-800 font-medium mb-1 text-sm md:text-base">お問い合わせありがとうございます</h3>
                  <p className="text-green-700 text-xs md:text-sm">
                    お送りいただいた内容を確認し、2営業日以内にご返信いたします。
                  </p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-xl md:text-2xl mr-3"></i>
                <div>
                  <h3 className="text-red-800 font-medium mb-1 text-sm md:text-base">送信エラー</h3>
                  <p className="text-red-700 text-xs md:text-sm">
                    {submitErrorMessage || '送信に失敗しました。しばらく経ってから再度お試しください。'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <form 
            id="contact-form"
            onSubmit={handleSubmit} 
            className="space-y-6"
          >
            <input type="text" name="_hp" value={hpValue} tabIndex={-1} autoComplete="off" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true" onChange={(e) => setHpValue(e.target.value)} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-xs md:text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 md:px-5 sm:px-6 lg:px-8 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#917c50] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                />
              </div>

              <div>
                <label 
                  htmlFor="email" 
                  className="block text-xs md:text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 md:px-5 sm:px-6 lg:px-8 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#917c50] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div>
                <label 
                  htmlFor="company" 
                  className="block text-xs md:text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  会社名・団体名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-5 sm:px-6 lg:px-8 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#917c50] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                />
              </div>

              <div>
                <label 
                  htmlFor="phone" 
                  className="block text-xs md:text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  お電話番号
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 md:px-5 sm:px-6 lg:px-8 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#917c50] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                />
              </div>
            </div>

            <div>
              <label 
                htmlFor="service" 
                className="block text-xs md:text-sm font-medium text-gray-700 mb-2"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                ご希望のサービス
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                className="w-full px-3 md:px-5 sm:px-6 lg:px-8 py-2 md:py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#917c50] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                <option value="">選択してください</option>
                <option value="企業研修・セミナー">企業研修・セミナー</option>
                <option value="プライベート執事サービス">プライベート執事サービス</option>
                <option value="マナー講座">マナー講座</option>
                <option value="コンサルティング">コンサルティング</option>
                <option value="その他">その他</option>
              </select>
            </div>

            <div>
              <label 
                htmlFor="message" 
                className="block text-xs md:text-sm font-medium text-gray-700 mb-2"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={6}
                maxLength={500}
                className="w-full px-3 md:px-5 sm:px-6 lg:px-8 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#917c50] focus:border-transparent transition-all duration-300 resize-vertical text-sm md:text-base"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                placeholder="ご質問やご要望について詳しくお聞かせください（500文字以内）"
              />
              <div className="text-right text-xs md:text-sm text-gray-500 mt-1">
                {formData.message.length}/500文字
              </div>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                onClick={handleShineClick}
                className="btn-gold bg-[#A26A1D] text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-medium hover:bg-[#8A5A18] transition-all duration-300 whitespace-nowrap text-sm md:text-base cursor-pointer"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 md:py-20 bg-[#f5f5f5]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 md:px-8">
          <h2 
            className="text-xl md:text-5xl font-light text-[#1a1a1a] text-center mb-12 md:mb-16"
            style={{ fontFamily: '"Noto Serif JP", serif' }}
          >
            Contact Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-mail-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                Email
              </h3>
              <p 
                className="text-gray-600 text-sm md:text-base"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                <a 
                  href="mailto:info@butlerbell.jp" 
                  className="hover:text-[#917c50] transition-colors duration-300 cursor-pointer"
                >
                  info@butlerbell.jp
                </a>
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-map-pin-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                Office Address
              </h3>
              <address 
                className="text-gray-600 not-italic text-sm md:text-base"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                〒135-0064<br />
                東京都江東区青海2丁目7−4
              </address>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-[#917c50] rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                <i className="ri-time-line text-white text-xl md:text-2xl"></i>
              </div>
              <h3 
                className="text-base md:text-xl font-medium text-[#1a1a1a] mb-3 md:mb-4"
                style={{ fontFamily: '"Noto Serif JP", serif' }}
              >
                Response Time
              </h3>
              <p 
                className="text-gray-600 text-sm md:text-base"
                style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
              >
                2営業日以内に<br />
                ご返信いたします
              </p>
            </div>
          </div>
          
          <div className="mt-8 md:mt-12 text-center">
            <p 
              className="text-xs md:text-sm text-gray-500"
              style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
            >
              お電話でのお問い合わせは、お問い合わせフォームよりご連絡ください
            </p>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
